"use client";

import React, { useEffect } from "react";
import { useTranslations } from "next-intl";
import { useEmailVerificationGuard } from "./EmailVerificationContext";

/**
 * Gates a route behind email verification *without navigating away from it*.
 *
 * The visitor is on this route because they asked to be - most sharply when
 * they registered from the Free Trial page's own prompt and were returned here
 * by `?redirect=`. Replacing the route with the home page threw that
 * destination away and made a successful sign-up look like it had failed;
 * they now stay put, the verification modal opens over this route, and the
 * moment the code is accepted `refetchUser()` (see useEmailVerification) flips
 * `is_active`, `isUserInactive` goes false, and the real content renders in
 * place - no second navigation, nothing to remember and return to.
 *
 * isUserInactive is resolved once, centrally, by EmailVerificationProvider
 * (via isAccountActive) - recomputing it here too would let this guard and
 * the provider's own auto-open effect drift out of sync with each other.
 */
export default function EmailVerificationGuard({ children }: { children: React.ReactNode }) {
  const { openVerificationModal, isUserInactive } = useEmailVerificationGuard();
  const t = useTranslations("inactiveAccount");
  const tLogin = useTranslations("login");

  useEffect(() => {
    if (isUserInactive) {
      // Deliberately no pending destination: this route IS the destination,
      // so there is nothing for handleVerificationSuccess() to navigate back
      // to. Reopening on mount is what covers arriving here after dismissing
      // the modal somewhere else - the provider is mounted once at the root
      // and its own auto-open effect only fires when `isUserInactive` itself
      // changes, which a navigation does not do.
      openVerificationModal();
    }
  }, [isUserInactive, openVerificationModal]);

  if (isUserInactive) {
    // Never the protected content, not even for a single frame. This stands in
    // for it so that dismissing the modal leaves a way back into the flow
    // rather than a blank page behind the closed dialog.
    return (
      <main className="min-h-screen bg-table py-16">
        <div className="mx-auto max-w-[920px] px-4">
          <div className="rounded-[32px] bg-card p-8 text-center shadow-sm ring-1 ring-border-subtle md:p-12">
            <h1 className="text-2xl font-bold text-primary md:text-3xl">
              {t("title")}
            </h1>
            <p className="mx-auto mt-4 max-w-2xl text-base leading-7 text-text-secondary">
              {t("description")}
            </p>
            <button
              type="button"
              onClick={() => openVerificationModal()}
              className="mt-8 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-on-primary transition hover:bg-secondary cursor-pointer"
            >
              {tLogin("verify_now")}
            </button>
          </div>
        </div>
      </main>
    );
  }

  return <>{children}</>;
}
